import React, { createContext, memo, useContext, useMemo, type ComponentProps, type ReactNode } from 'react';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../_placeholders';
import { ChevronDown } from '@core/icons-react';
import { cn } from '../../../utils/classnames';

type ChainOfThoughtContextValue = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
};

const ChainOfThoughtContext = createContext<ChainOfThoughtContextValue | null>(null);

const useChainOfThought = () => {
  const context = useContext(ChainOfThoughtContext);
  if (!context) {
    throw new Error('ChainOfThought components must be used within ChainOfThought');
  }
  return context;
};

export type ChainOfThoughtProps = ComponentProps<'div'> & {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const ChainOfThought = memo(
  ({
    className,
    open,
    defaultOpen = false,
    onOpenChange,
    children,
    ...props
  }: ChainOfThoughtProps) => {
    const [isOpen, setIsOpen] = useControllableState({
      prop: open,
      defaultProp: defaultOpen,
      onChange: onOpenChange,
    });

    const chainOfThoughtContext = useMemo(
      () => ({ isOpen, setIsOpen }),
      [isOpen, setIsOpen]
    );

    return (
      <ChainOfThoughtContext.Provider value={chainOfThoughtContext}>
        <div
          className={cn('core-chain-of-thought', className)}
          {...props}
        >
          {children}
        </div>
      </ChainOfThoughtContext.Provider>
    );
  }
);

export type ChainOfThoughtHeaderProps = ComponentProps<typeof CollapsibleTrigger>;

export const ChainOfThoughtHeader = memo(
  ({ className, children, ...props }: ChainOfThoughtHeaderProps) => {
    const { isOpen, setIsOpen } = useChainOfThought();

    return (
      <Collapsible onOpenChange={setIsOpen} open={isOpen}>
        <CollapsibleTrigger
          className={cn('core-chain-of-thought-header', className)}
          {...props}
        >
          <div className="core-chain-of-thought-header__icon">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 2C6.34315 2 5 3.34315 5 5C5 5.74028 5.26756 6.41652 5.70711 6.94444C4.73056 7.38889 4 8.39815 4 9.55556C4 11.0046 5.00736 12.2222 6.33333 12.2222H9.66667C10.9926 12.2222 12 11.0046 12 9.55556C12 8.39815 11.2694 7.38889 10.2929 6.94444C10.7324 6.41652 11 5.74028 11 5C11 3.34315 9.65685 2 8 2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            </svg>
          </div>
          <span className="core-chain-of-thought-header__text">
            {children ?? 'Chain of Thought'}
          </span>
          <ChevronDown
            className={cn(
              'core-chain-of-thought-header__chevron',
              isOpen && 'core-chain-of-thought-header__chevron--open'
            )}
            size={16}
          />
        </CollapsibleTrigger>
      </Collapsible>
    );
  }
);

export type ChainOfThoughtStepProps = ComponentProps<'div'> & {
  icon?: React.ComponentType<any>;
  label: ReactNode;
  description?: ReactNode;
  status?: 'complete' | 'active' | 'pending';
};

const DotIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
    <circle cx="8" cy="8" r="4" />
  </svg>
);

export const ChainOfThoughtStep = memo(
  ({
    className,
    icon: Icon = DotIcon,
    label,
    description,
    status = 'complete',
    children,
    ...props
  }: ChainOfThoughtStepProps) => (
    <div
      className={cn(
        'core-chain-of-thought-step',
        `core-chain-of-thought-step--${status}`,
        className
      )}
      {...props}
    >
      <div className="core-chain-of-thought-step__icon-wrapper">
        <Icon size={16} />
        <div className="core-chain-of-thought-step__connector" />
      </div>
      <div className="core-chain-of-thought-step__content">
        <div className="core-chain-of-thought-step__label">{label}</div>
        {description && (
          <div className="core-chain-of-thought-step__description">{description}</div>
        )}
        {children}
      </div>
    </div>
  )
);

export type ChainOfThoughtSearchResultsProps = ComponentProps<'div'>;

export const ChainOfThoughtSearchResults = memo(
  ({ className, ...props }: ChainOfThoughtSearchResultsProps) => (
    <div className={cn('core-chain-of-thought-search-results', className)} {...props} />
  )
);

export type ChainOfThoughtSearchResultProps = ComponentProps<'div'>;

export const ChainOfThoughtSearchResult = memo(
  ({ className, children, ...props }: ChainOfThoughtSearchResultProps) => (
    <div
      className={cn('core-chain-of-thought-search-result', className)}
      {...props}
    >
      {children}
    </div>
  )
);

export type ChainOfThoughtContentProps = ComponentProps<typeof CollapsibleContent>;

export const ChainOfThoughtContent = memo(
  ({ className, children, ...props }: ChainOfThoughtContentProps) => {
    const { isOpen } = useChainOfThought();

    return (
      <Collapsible open={isOpen}>
        <CollapsibleContent
          className={cn('core-chain-of-thought-content', className)}
          {...props}
        >
          {children}
        </CollapsibleContent>
      </Collapsible>
    );
  }
);

export type ChainOfThoughtImageProps = ComponentProps<'div'> & {
  caption?: string;
};

export const ChainOfThoughtImage = memo(
  ({ className, children, caption, ...props }: ChainOfThoughtImageProps) => (
    <div className={cn('core-chain-of-thought-image', className)} {...props}>
      <div className="core-chain-of-thought-image__wrapper">
        {children}
      </div>
      {caption && <p className="core-chain-of-thought-image__caption">{caption}</p>}
    </div>
  )
);

ChainOfThought.displayName = 'ChainOfThought';
ChainOfThoughtHeader.displayName = 'ChainOfThoughtHeader';
ChainOfThoughtStep.displayName = 'ChainOfThoughtStep';
ChainOfThoughtSearchResults.displayName = 'ChainOfThoughtSearchResults';
ChainOfThoughtSearchResult.displayName = 'ChainOfThoughtSearchResult';
ChainOfThoughtContent.displayName = 'ChainOfThoughtContent';
ChainOfThoughtImage.displayName = 'ChainOfThoughtImage';





